export interface ReferencePointOfInterest {
  locationId: number;
  ICAO?: string;
  IATA?: string;
  name?: string;
  other?: string;
  latitude: number;
  longitude: number;
  locationTypeId: number;
  source: string;
}

export interface CustomPointOfInterest {
  locationId: number;
  organisationId: string;
  code?: string;
  name: string;
  latitude: number;
  longitude: number;
  locationTypeId: number;
  source: string;
}

export type PointOfInterest = ReferencePointOfInterest | CustomPointOfInterest;

export enum EventType {
  FLIGHT = 'FLIGHT',
  POWER = 'POWER',
  ENGINE = 'ENGINE',
  ENGINEPARAMETER = 'ENGINE_PARAMETER',
  MANIFEST = 'MANIFEST'
}

export enum EventSubtype {
  BlocksOff = 'BLOCKS_OFF',
  BlocksOn = 'BLOCKS_ON',
  HoverStart = 'HOVER_START',
  HoverStop = 'HOVER_STOP',
  Landing = 'LANDING',
  Takeoff = 'TAKEOFF',
  TaxiStart = 'TAXI_START',
  TaxiStop = 'TAXI_STOP',
  TouchAndGoLanding = 'TOUCH_AND_GO_LANDING',
  TouchAndGoTakeoff = 'TOUCH_AND_GO_TAKEOFF',
  Off = 'OFF',
  On = 'ON',
  Restart = 'RESTART',
  Restored = 'RESTORED'
}

interface BaseFlightLogsEvent {
  eventId: string;
  eventType: EventType;
  eventSubtype: EventSubtype;
  eventTime: number;
  eventConfidence: number;
  version: number;
}

// Base type for events with location
interface LocationFlightLogsEvent<T extends EventType, S extends EventSubtype>
  extends BaseFlightLogsEvent {
  eventType: T;
  eventSubtype: S;
  source: string;
  isBackfill: boolean;
  longitude: number;
  latitude: number;
}

// Type helper for adding location info
type FlightLogsEventWithLocation<T extends LocationFlightLogsEvent<EventType, EventSubtype>> = Omit<
  T,
  'locationId'
> & {
  pointOfInterest?: PointOfInterest;
};

interface FlightEvent extends LocationFlightLogsEvent<EventType.FLIGHT, EventSubtype> {}

interface PowerEvent extends LocationFlightLogsEvent<EventType.POWER, EventSubtype> {}

interface EngineEvent extends LocationFlightLogsEvent<EventType.ENGINE, EventSubtype> {}

interface ManifestEvent extends BaseFlightLogsEvent {
  source: string;
  sourceValue: string;
  detectedId: string;
}

interface EngineParameterEvent extends BaseFlightLogsEvent {
  value: number;
  unit: string;
}

export type FlightLogsEvent =
  | FlightLogsEventWithLocation<FlightEvent>
  | FlightLogsEventWithLocation<PowerEvent>
  | FlightLogsEventWithLocation<EngineEvent>;

export interface FlightlogsForDeviceTrackFromAPI {
  serialNumber: string;
  bootCount: number;
  flightId: string;
  aircraftId: string;
  /* power events related to the device(spider) */
  powerEvents: FlightLogsEventWithLocation<PowerEvent>[];
  /* engine events related to the flight */
  engineEvents: FlightLogsEventWithLocation<EngineEvent>[];
  /* engine parameter events(aggregate metrics related to the engine) related to the flight */
  engineParameterEvents: EngineParameterEvent[];
  flightEvents: FlightLogsEventWithLocation<FlightEvent>[];
  manifestEvents: ManifestEvent[];
}
